import cx from "classnames";
import { Suspense, lazy, useState } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { ToastTypes, withToast } from "../../contexts/toastr.context";
import { Loader } from "../../_components";
import { Checkbox } from "../../_components/form-controls/checkbox.component";
import { NL2BR } from "../../_components/nl2br.component";
import PremiumFeature from "../../_components/premium/premium-feature.component";
import Label from "../../_components/typography/label.typography";
import UIcon from "../../_components/uicon-component";
import constants from "../../_config/constants.config";
import continents from "../../_config/country-codes.config.json";
import { CommunityService } from "../../_service";
import { setCommunity } from "../../_store/_actions/community.actions";
import DefaultJoinGroup from "./community-experience-setting/default-group-join.section";
import { useAppService } from "../../hooks/use-app-service";
const FontConfiguration = lazy(() => import("../../_components/font-configuration.component"));
const CommunityConfigurable = lazy(() => import("../../_components/atoms/community-configurable.component"));

export const COMMUNITY_SETTING_PAGE_ROUTE = "/settings/communityexperience";

export function CommunitySettingsPageComponent({
  user,
  community,
  communityPac,
  updateCommunity,
  addToast,
}: any) {
  return (
    <PremiumFeature
      isAvailable={communityPac.communityCustomization}
      hideFeature>
      <div className="UserWallPage Card rounded shadow theme-bg-surface">
        {/* headers */}
        <div className="pl-4 pr-2 py-4 rounded-t  border-b theme-border-default flex items-center justify-between">
          <span className="font-bold theme-text-heading-2">
            {community.name} Community Settings
          </span>
          <div className="theme-text-subtitle-1">ID: {community.id}</div>
        </div>
        <div className="card p-2 pt-0 theme-text-heading-2 mt-2">
          <CommunityConfigLists
            user={user}
            community={community}
            updateCommunity={updateCommunity}
            addToast={addToast}
            communityPac={communityPac}
          />
          {/* advanced configurable list */}
          <Suspense fallback={<></>}>
           <CommunityConfigurablesList community={community} />
          </Suspense>
        </div>
      </div>
    </PremiumFeature>
  );
}

function LoginRequired({ user, community, updateCommunity, addToast, eventName }: any) {
  const [isLoading, setIsLoading] = useState(false);
  // const [operationSuccessful, setOperationSuccessful] = useState(null);

  const { analyticsService } = useAppService();

  const toggleCommunityLoginRequirement = () => {
    if (isLoading) return;

    setIsLoading(true);
    // setOperationSuccessful(null);
    CommunityService.toggleCommunityLoginRequirement(
      user,
      community.id,
      community.isLoginRequired ? false : true
    )
      .then(({ community }) => {
        // addToast("Updated", "", ToastTypes.success);
        updateCommunity(community);
        setIsLoading(false);
        analyticsService.track("make-community-private", {
          checkboxSelected: community.isLoginRequired
        })
        // setOperationSuccessful(true);
      })
      .catch((err) => {
        addToast("Failed to do so.", "", ToastTypes.danger);
        setIsLoading(false);
        // setOperationSuccessful(false);
      });
  };

  return (
    <div className="py-2">
      <div
        className={cx("flex cursor-pointer select-none", {
          "cursor-wait": isLoading,
        })}
        onClick={toggleCommunityLoginRequirement}>
        {isLoading ? (
          <div className="w-6 h-4">
            <Loader />
          </div>
        ) : (
          <Checkbox selected={community.isLoginRequired ? true : false} />
        )}
        <div className="mx-2">
          <div>Make your community private</div>
          <div className="text-xs theme-text-subtitle-2 mt-1">
            <NL2BR text="Users will have to log in or sign up to access the community" />
          </div>
        </div>
        <div />
      </div>
    </div>
  );
}

function CommunityConfigLists({
  user,
  community,
  communityPac,
  updateCommunity,
  addToast,
}: any) {
  return (
    <>
      {/* Onboarding list */}
      <CommunityConfigList
        header="Onboarding"
        configList={[
          {
            key: "isLoginRequired",
            isPremiumAvailable: true,
            label: "Make your community private",
            subtitle:
              "Users will have to log in or sign up to access the community",
            component: (
              <LoginRequired
                user={user}
                community={community}
                updateCommunity={updateCommunity}
                addToast={addToast}
                eventName="make-community-private"
              />
            ),
          },
          // {
          //   key: "isMobileAndEmailVerificationRequired",
          //   isPremiumAvailable: true,
          //   label: "Make account verification mandatory for user",
          //   subLabel:
          //     "New user will have to verify both email & mobile no. to access the community",
          //   warning:
          //     "Admin will also have to verify their mobile no. & email or else they won't be able to access the community",
          // },
          {
            key: "needsAdminVerification",
            isPremiumAvailable: true,
            label: "Gated community access",
            subLabel:
              "Admin will have to approve or reject user's requests to join the community",
            eventName: "gated-community-access",
          },
          {
            key: "disableMobileLogin",
            isPremiumAvailable: true,
            label: "Disable Mobile Login/Register",
            isV2: true,
          },
          {
            key: "disableEmailLogin",
            isPremiumAvailable: true,
            label: "Disable Email Login/Register",
            isV2: true,
          },
          {
            key: "disableGoogleLogin",
            isPremiumAvailable: true,
            label: "Disable Google Login/Register",
            isV2: true,
          },
          {
            key: "disableFacebookLogin",
            isPremiumAvailable: true,
            label: "Disable Facebook Login/Register",
            isV2: true,
          },
          {
            key: "setExploreGroupsAsLandingPageForNonLoggedinUsers",
            isPremiumAvailable: true,
            label:
              "Non Logged in user will land on explore group page instead of post directory",
            eventName: "nonlog-checkbox-land-explore",
            isV2: true,
            isExperimental: true,
          },
        ]}
        user={user}
        community={community}
        updateCommunity={updateCommunity}
        addToast={addToast}
      />
      <DefaultJoinGroup />
      {/* Show & Hide features */}
      <CommunityConfigList
        header="Show & Hide features"
        configList={[
          {
            key: "isDirectMessageDisabled",
            isPremiumAvailable: true,
            label: "Hide Direct Chat",
            subLabel:
              "Users will not be able to send a direct message to each other",
            eventName: "hide-direct-chat"
          },
          {
            key: "isExploreCourseDisabled",
            isPremiumAvailable: true,
            label: "Hide Courses",
            subLabel: "Users will not be able to see Courses module",
            eventName: "hide-course",
            isV2: true,
          },
          {
            key: "isEventsTabDisabled",
            isPremiumAvailable: true,
            label: "Hide Events tab",
            subLabel: "Users will not be able to see events tab",
            eventName: "hide-events",
            isV2: true,
          },
          {
            key: "disableMobileViewHeaderDropdown",
            isPremiumAvailable: true,
            label:
              "Hide profile picture from the top right corner for mobile responsive view",
            subLabel: "Useful when using community in an in-app browser",
            eventName:"hide-profile-picture-in-mobile-view",
            isV2: true,
          },
          {
            key: "disableHeaderDropdown",
            isPremiumAvailable: true,
            label:
              "Hide profile picture from the top right corner on every view",
            eventName: "hide-profile-picture-in-every-view",
            isV2: true,
          },
          {
            key: "disableHeaderDropdown",
            isPremiumAvailable: true,
            label: "Hide Logout button from profile drop down",
            eventName: "hide-logout-button",
            subLabel: "Useful for integrated community, especially using SSO",
          },
          {
            key: "hideHeaderCommunityLogo",
            isPremiumAvailable: true,
            label: "Hide the community logo from the header",
            eventName: "hide-community-logo",
            isV2: true,
          },
          {
            key: "hideHeaderCommunityName",
            isPremiumAvailable: true,
            label: "Hide community title from the header",
            eventName: "hide-community-title",
            isV2: true,
          },
          {
            key: "collapseSidebarGroupLinks",
            isPremiumAvailable: true,
            label: "Show collapsed group in the side nav bar",
            eventName: "default-collapsed-groups",
            isV2: true,
          },
          {
            key: "expandPostDirectory",
            isPremiumAvailable: true,
            label: "Show full expanded post in the home feed",
            eventName: "default-expanded-post"
          },
          {
            key: "hideHeaderLeaderboardPill",
            isPremiumAvailable: true,
            label: "Hide the leaderboard score from the header",
            eventName: "hide-karmpoints",
            isV2: true,
          },
          {
            key: "showPoweredByPensil",
            isPremiumAvailable: communityPac.hidePoweredByPensil,
            label: "Show powered by Pensil",
            eventName: "show-powered-by-pensil",
            isWhiteLabel: community.configuration.isWhiteLabel,
            isV2: true,
            isExperimental: false,
          },
          {
            key: "hideClosedGroupWithoutJoin",
            isPremiumAvailable: true,
            label: "Hide Closed Groups Without Join",
            subLabel:
              "Closed groups would not be shown to the user if they are not a member of the group",
            eventName: "hide-closed-group-without-join"
          },
          {
            key: "hideCommunitySubscribersList",
            isPremiumAvailable: true,
            label: "Hide Community subscriber count from the users",
            subLabel:
              "This will hide the community subscriber list in the community header",
            isV2: true,
          },
          {
            key: "isCommunitySwitcherDisabled",
            isPremiumAvailable: true,
            label: "Disable Community Switcher",
            eventName: "hide-community-switcher"
          },
          {
            key: "useMdEditorForCreatePostDescription",
            isPremiumAvailable: true,
            label: "Use MD Editor for create post description",
            eventName: "use-MD-editor",
            isV2: true,
            isExperimental: true,
          },
          {
            key: "disableEmailNotifications",
            label: "Disable all email notifications to members",
            eventName: "disable-all-emails",
            isV2: true,
          },
          {
            key: "anyoneCanCreateEvent",
            label: "Anyone can create event in community",
            eventName: "anyone-can-create-event",
            isV2: true,
          },
          {
            key: "hideCommunityUnsubscribe",
            label: "Hide unsubscribe button in community",
            eventName: "hide-unsubscribe-button",
            isV2: true,
          },
          {
            key: "showCommunityLinksInHeader",
            label: "Show Community links as Navlinks",
            eventName: "show-quick-links-in-nav-bar",
            isV2: true,
          },
        ]}
        user={user}
        community={community}
        updateCommunity={updateCommunity}
        addToast={addToast}
      />
      {/* Other */}
      {/* <CommunityConfigList
        header="Onboarding"
        configList={[
          {
            key: "setExploreGroupsAsLandingPageForNonLoggedinUsers",
            isPremiumAvailable: true,
            label:
              "Non Logged in user will land on explore group page instead of post directory",
            isV2: true,
            isExperimental: true,
          },
        ]}
        user={user}
        community={community}
        updateCommunity={updateCommunity}
        addToast={addToast}
      /> */}
    </>
  );
}

export function CommunityConfigList({
  header = "Community Configurations",
  configList,
  user,
  community,
  updateCommunity,
  addToast,
}: any) {
  return (
    <details className="my-2 rounded group" open>
      <summary className="flex justify-between font-semibold p-2 theme-bg-disable cursor-pointer">
        {header}
        <div className="transition group-open:rotate-180 w-12 flex place-content-center">
          <UIcon icon={"angle-small-up"} className={"h-4"} />
        </div>
      </summary>

      <div className={cx("border theme-border-default px-2 theme-bg-default")}>
        {configList.map((config: any, i: number) =>
          config.isV2 ? (
            config.component ? (
              <div key={i}>{config.component}</div>
            ) : (
              <div key={i}>
                {config.isWhiteLabel === true ? null : (
                  <CommunityConfigV2
                    key={config.key}
                    configKey={config.key}
                    label={config.label}
                    subLabel={config.subLabel}
                    warning={config.warning}
                    isExperimental={config.isExperimental}
                    user={user}
                    community={community}
                    updateCommunity={updateCommunity}
                    addToast={addToast}
                    isPremiumAvailable={config.isPremiumAvailable}
                    eventName={config.eventName}
                  />
                )}
              </div>
            )
          ) : config.component ? (
            <div key={i}>{config.component}</div>
          ) : (
            <div key={i}>
              <CommunityConfig
                key={config.key}
                configKey={config.key}
                label={config.label}
                subLabel={config.subLabel}
                warning={config.warning}
                user={user}
                community={community}
                updateCommunity={updateCommunity}
                addToast={addToast}
                isPremiumAvailable={config.isPremiumAvailable}
                eventName={config.eventName}
              />
            </div>
          )
        )}
      </div>
    </details>
  );
}

/**
 *
 * @param {*} param0
 * @returns
 */
function CommunityConfig({
  configKey,
  label = "!!This is only a placeholder text!!",
  subLabel = "",
  warning = "",
  user,
  community,
  updateCommunity,
  addToast,
  isPremiumAvailable = true,
  eventName
}: any) {
  const { analyticsService } = useAppService();

  const [isLoading, setIsLoading] = useState(false);
  // const [operationSuccessful, setOperationSuccessful] = useState(null);

  const updateConfig = () => {
    if (isLoading) return;

    setIsLoading(true);
    // setOperationSuccessful(null);
    CommunityService.updateConfig(
      user,
      community.id,
      configKey,
      community[configKey] ? false : true
    )
      .then(({ community }) => {
        // addToast("Updated", "", ToastTypes.success);
        updateCommunity(community);
        setIsLoading(false);
        // setOperationSuccessful(true);
        eventName && analyticsService.track(eventName, {
          checkboxSelected: community[configKey]
        });
      })
      .catch((err) => {
        addToast("Failed to do so.", "", ToastTypes.danger);
        setIsLoading(false);
        // setOperationSuccessful(false);
      });
  };

  return (
    <div className="py-2 flex">
      <div
        className={cx("flex cursor-pointer select-none", {
          "cursor-wait": isLoading,
        })}
        onClick={updateConfig}>
        {isLoading ? (
          <div className="w-6 h-4">
            <Loader />
          </div>
        ) : (
          <Checkbox selected={community[configKey] ? true : false} />
        )}
        <div className="mx-2">
          <div>{label}</div>
          {subLabel ? (
            <div className="text-xs theme-text-subtitle-2 mt-1">
              <NL2BR text={subLabel} />
            </div>
          ) : (
            <></>
          )}
          {warning ? (
            <div className="text-xs text-red-500 mt-1">
              <NL2BR text={warning} />
            </div>
          ) : (
            <></>
          )}
        </div>
        {/* {
                operationSuccessful !== null ? <Tick minimal success={operationSuccessful} /> : null
            } */}
      </div>
    </div>
  );
}
/**
 *
 * @param {*} param0
 * @returns
 */
function CommunityConfigV2({
  configKey,
  label = "!!This is only a placeholder text!!",
  subLabel = "",
  warning = "",
  user,
  community,
  updateCommunity,
  addToast,
  isExperimental = false,
  isPremiumAvailable = true,
  eventName
}: any) {
  const { analyticsService } = useAppService();

  const [isLoading, setIsLoading] = useState(false);
  // const [operationSuccessful, setOperationSuccessful] = useState(null);

  const updateConfig = () => {
    if (isLoading) return;

    setIsLoading(true);
    // setOperationSuccessful(null);
    CommunityService.updateConfigV2(
      user,
      community.id,
      configKey,
      community?.configuration[configKey] ? false : true
    )
      .then(({ community }) => {
        // addToast("Updated", "", ToastTypes.success);
        updateCommunity(community);
        setIsLoading(false);
        // setOperationSuccessful(true);
        eventName && analyticsService.track(eventName, {
          checkboxSelected: community?.configuration[configKey]
        });
      })
      .catch((err) => {
        addToast("Failed to do so.", "", ToastTypes.danger);
        setIsLoading(false);
        // setOperationSuccessful(false);
      });
  };

  return (
    <PremiumFeature isAvailable={isPremiumAvailable}>
      <div className="py-2 flex items-center">
        <div
          className={cx("flex cursor-pointer select-none", {
            "cursor-wait": isLoading,
          })}
          onClick={updateConfig}>
          {isLoading ? (
            <div className="w-6 h-4">
              <Loader />
            </div>
          ) : (
            <Checkbox
              selected={community?.configuration[configKey] ? true : false}
            />
          )}
          <div className="mx-2">
            <div>
              {label}{" "}
              <span className="text-xs theme-text-subtitle-2">V2 Feature</span>
            </div>
            {subLabel ? (
              <div className="text-xs theme-text-subtitle-2 mt-1">
                <NL2BR text={subLabel} />
              </div>
            ) : (
              <></>
            )}
            {warning ? (
              <div className="text-xs text-red-500 mt-1">
                <NL2BR text={warning} />
              </div>
            ) : (
              <></>
            )}
            {isExperimental ? (
              <div className="text-red-500">This feature is experimental.</div>
            ) : (
              <></>
            )}
          </div>
          {/* {operationSuccessful !== null ? <Tick minimal success={operationSuccessful} /> : null} */}
        </div>
      </div>
    </PremiumFeature>
  );
}

function CommunityConfigurablesList({ community }: any) {
  return (
    <details className="my-2 rounded group" open>
      <summary className="flex justify-between font-semibold p-2 theme-bg-disable cursor-pointer">
        Advanced Configurables
        <div className="transition group-open:rotate-180 w-12 flex place-content-center">
          <UIcon icon={"angle-small-up"} className={"h-4"} />
        </div>
      </summary>
      <div className={cx("border theme-border-default px-2 theme-bg-default")}>
        {/* default country code */}
        <div className="flex items-center justify-between border theme-border-default rounded-lg theme-bg-default p-4 w-full flex-grow focus:outline-none resize-none my-4">
          <Suspense fallback={<></>}>
            <CommunityConfigurable
              community={community}
              configurableKey={constants.COMMUNITY_DEFAULT_COUNTRY_CODE}
              title="Community Default Country Code"
              subTitle="This will show up by default on login/signup."
              inputType="select"
              options={continents}
            />
          </Suspense>
        </div>
        {/* default google font */}
        <Suspense fallback={<></>}>
         <FontConfiguration community={community} />
        </Suspense>
        <div className="flex flex-col  justify-between border theme-border-default rounded-lg theme-bg-default p-4 w-full flex-grow focus:outline-none resize-none my-4">
            <Suspense fallback={<></>}>
              <CommunityConfigurable
              community={community}
              configurableKey={constants.COMMUNITY_DEFAULT_LANGUAGE}
              title="Community Default Language"
              subTitle=""
              inputType="select"
            />
          </Suspense>
          <Label size="caption" className="mt-2">
            Language changes are only applicable on the community home, it
            doesn't effect this setting page. Go to community home to see the
            changes
          </Label>
        </div>
      </div>
    </details>
  );
}

const CommunitySettingsPage = withToast(
  connect(
    (s: any) => ({
      user: s.auth,
      community: s.community,
      communityPac: s.communityPac,
    }),
    (d) =>
      bindActionCreators(
        {
          updateCommunity: setCommunity,
        },
        d
      )
  )(CommunitySettingsPageComponent)
);

export default CommunitySettingsPage;
